import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  Renderer2,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router, UrlTree } from '@angular/router';
import { TuiHostedDropdownComponent, TuiAlertService } from '@taiga-ui/core';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Subscription, firstValueFrom, forkJoin, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import * as moment from 'moment-timezone';

import { ModalDuplicateServiceComponent } from 'src/app/shared/modal-duplicate-service/modal-duplicate-service.component';
import { ModalSendMessageComponent } from '../modal-send-message/modal-send-message.component';
import { PendingServicesService } from 'src/app/services/pending-services/pending-services.service';
import {
  Communication,
  CommunicationState,
  PaginatedRequest,
  PaginatedResponse,
  PendingService,
  PendingServiceRow,
  ProductFilterItem,
} from 'src/app/models/pending-services/pending-services.model';

import { ServicesService } from 'src/app/services/service/services.service';
import { ClientService } from 'src/app/services/client/client.service';
import { Zone } from 'src/app/models/zone/zone.model';
import { HistoryServicesService } from 'src/app/services/history-services/history-services.service';
import { searchIcon } from 'src/app/utils/icons-path';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { LocalStorageService } from 'src/app/services/local-storage/local-storage.service';
import { LocalStorageKeys } from 'src/app/enum/local-storage-keys.enum';
import { Client } from 'src/app/models/service-detail/service-detail.model';
import { FiltersPersistenceService } from 'src/app/services/filters/filters-persistence.service';
import {
  VehicleFuelLabelKeys,
  VehicleFuelTypeEnum,
} from 'src/app/enum/vehicle-fuel.enum';
import { vehicleChasisTypeWithTranslations } from 'src/app/constants/data';

export interface CommView {
  icon: string;
  hintKey: string;
  color: string;
}

export const COMM_PALETTE: Record<CommunicationState, string> = {
  SMS_SENT: 'var(--Day-Status-success-bg,  rgba(74,201,155,.12))',
  SMS_ERROR: 'var(--Day-Status-error-bg,    rgba(244,87,37,.12))',
  WHATSAPP_WAITING: 'var(--Day-Status-info-bg,     rgba(112,182,246,.12))',
  WHATSAPP_ERROR: 'var(--Day-Status-error-bg,    rgba(244,87,37,.12))',
  WHATSAPP_NO_ANSWER: 'var(--Day-Status-warning-bg,  rgba(255,199,0,.12))',
  WHATSAPP_ANSWERED: 'var(--Day-Status-success-bg,  rgba(74,201,155,.12))',
  PHONE_INCIDENT: 'var(--Day-Status-error-bg,    rgba(244,87,37,.12))',
  NOT_CONTACTED: 'rgba(191,197,204,.20)',
  SMS: 'rgba(112,182,246,.12)',
  WHATSAPP: 'rgba(37,211,102,.12)',
};

export const COMM_ICONS: Record<CommunicationState, CommView> = {
  SMS_SENT: {
    icon: 'assets/icons/communication-icons/sms_sent.svg',
    hintKey: 'communication.SMS_SENT',
    color: COMM_PALETTE.SMS_SENT,
  },

  SMS_ERROR: {
    icon: 'assets/icons/communication-icons/error_sending_sms.svg',
    hintKey: 'communication.SMS_ERROR',
    color: COMM_PALETTE.SMS_ERROR,
  },

  WHATSAPP_WAITING: {
    icon: 'assets/icons/communication-icons/waiting_answer.svg',
    hintKey: 'communication.WHATSAPP_WAITING',
    color: COMM_PALETTE.WHATSAPP_WAITING,
  },

  WHATSAPP_ERROR: {
    icon: 'assets/icons/communication-icons/error_sending.svg',
    hintKey: 'communication.WHATSAPP_ERROR',
    color: COMM_PALETTE.WHATSAPP_ERROR,
  },

  WHATSAPP_NO_ANSWER: {
    icon: 'assets/icons/communication-icons/not_answered_in_time.svg',
    hintKey: 'communication.WHATSAPP_NO_ANSWER',
    color: COMM_PALETTE.WHATSAPP_NO_ANSWER,
  },

  WHATSAPP_ANSWERED: {
    icon: 'assets/icons/communication-icons/answered.svg',
    hintKey: 'communication.WHATSAPP_ANSWERED',
    color: COMM_PALETTE.WHATSAPP_ANSWERED,
  },

  PHONE_INCIDENT: {
    icon: 'assets/icons/communication-icons/phone_incident.svg',
    hintKey: 'communication.PHONE_INCIDENT',
    color: COMM_PALETTE.PHONE_INCIDENT,
  },

  NOT_CONTACTED: {
    icon: '',
    hintKey: '',
    color: COMM_PALETTE.NOT_CONTACTED,
  },
  SMS: { icon: '', hintKey: '', color: COMM_PALETTE.SMS },
  WHATSAPP: { icon: '', hintKey: '', color: COMM_PALETTE.WHATSAPP },
};

const SERVICE_TYPE_TRANSLATION_KEY: Record<string | number, string> = {
  1: 'modals.add_service.option_home_mount',         // Servicio en domicilio
  2: 'modals.add_service.option_no_mobility_with_mount',     // Servicio en taller
  3: 'modals.add_service.option_shipping',             // Envío a domicilio
  4: 'modals.add_service.option_mobility_with_mount',  // Movilidad con montaje
};

type ProductFilterDisplay = ProductFilterItem & { ProductLabel?: string };

@Component({
  selector: 'app-pending-services',
  templateUrl: './pending-services.component.html',
  styleUrls: ['./pending-services.component.css'],
})
export class PendingServicesComponent implements OnInit, OnDestroy {
  @ViewChild(TuiHostedDropdownComponent)
  private readonly dropdown?: TuiHostedDropdownComponent;

  pendingRows: PendingServiceRow[] = [];
  displayedRows: PendingServiceRow[] = [];

  readonly pageSize = 25;
  pageIndex = 0;
  totalPages = 1;
  totalItems = 0;

  sortColumn: keyof PendingServiceRow | '' = '';
  sortDirection: 1 | -1 = -1;

  exportXlsx = false;
  exportCsv = false;
  sending = false;
  open = false;
  exportInProgress = false;

  loadingRows = new Set<string>();

  searchIcon = searchIcon;

  private readonly dateRangeControl = this.fb.control<[Date, Date] | null>(
    null,
    Validators.required
  );

  readonly filtersForm = this.fb.group({
    search: [{ value: '', disabled: true }],
    zones: [{ value: [], disabled: true }],
    dateRange: this.dateRangeControl,
    clients: [{ value: null, disabled: true }],
    products: [{ value: [], disabled: true }],
    // communication temporarily disabled
  });

  public sendingMethod: 'WhatsApp' | 'SMS' | null = null;

  commCounters: Record<CommunicationState, number> = {
    NOT_CONTACTED: 0,
    PHONE_INCIDENT: 0,
    SMS: 0,
    SMS_SENT: 0,
    SMS_ERROR: 0,
    WHATSAPP: 0,
    WHATSAPP_WAITING: 0,
    WHATSAPP_ANSWERED: 0,
    WHATSAPP_NO_ANSWER: 0,
    WHATSAPP_ERROR: 0,
  };

  zones: Zone[] = [];
  allClients: Client[] = [];
  products: ProductFilterDisplay[] = [];
  filteredRows: PendingServiceRow[] = [];

  isLoading = false;
  isInitializing = false;
  loadingProducts = false;
  dropdownOpen = false;
  isSearching = false;
  private isSelectAllActive = false;
  private manuallyDeselectedIds = new Set<string>();

  commCountersSorted: Array<{
    key: CommunicationState;
    value: number;
    color: string;
    label?: string;
  }> = [];

  private readonly sub = new Subscription();
  private readonly destroy$ = new Subject<void>();

  readonly columns: string[] = [
    'selected',
    'licensePlate',
    'time',
    'orderHash',
    'zone',
    'date',
    'product',
    'category',
    'isAPoolService',
    'client',
    'communication',
    'pickup',
    'delivery',
    'driver',
    'comment',
    'totalPrice',
    'hasDeliveryNote',
    'actions',
  ];

  rowTrack = (_: number, row: PendingServiceRow): string => row.serviceGuid;

  constructor(
    private readonly pendingServicesService: PendingServicesService,
    private readonly offcanvas: NgbOffcanvas,
    private readonly alerts: TuiAlertService,
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly rd: Renderer2,
    private readonly svc: ServicesService,
    private readonly clientService: ClientService,
    private readonly historyService: HistoryServicesService,
    private readonly translate: TranslateService,
    private readonly localStorageService: LocalStorageService,
    private readonly filtersPersistence: FiltersPersistenceService
  ) {}

  async ngOnInit(): Promise<void> {
    this.startLoading();
    await this.getProductsFilter();
    this.restoreFilters();

    this.sub.add(
      this.translate.onLangChange.subscribe((ev: LangChangeEvent) => {
        this.retranslateProductLabels(ev.lang);
      })
    );

    this.setupFiltersWatcher();
    this.watchDateRangeChanges();
    const initialRange = this.getInitialDateRange();
    this.dateRangeControl.setValue(initialRange, { emitEvent: false });
    this.loadLookupsAndFirstPage(initialRange);
  }

  private restoreFilters(): void {
    const defaultFilters = {
      search: '',
      zones: null,
      clients: null,
      products: null,
    };
    const filters = this.filtersPersistence.restore(
      'pendingServicesFilters',
      defaultFilters
    );
    this.filtersForm.patchValue(filters, { emitEvent: false });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    this.filtersPersistence.clear(LocalStorageKeys.PENDINGSERVICESFILTERS);
  }

  private startLoading(): void {
    this.isLoading = true;
    this.isInitializing = true;
    this.filtersForm.disable({ emitEvent: false });
  }

  private setupFiltersWatcher(): void {
    const searchControl = this.filtersForm.get('search');
    if (searchControl) {
      searchControl.valueChanges
        .pipe(
          debounceTime(700),
          distinctUntilChanged(),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.persistFilters();
          this.pageIndex = 0;
          this.loadPage(0, {
            showLoader: false,
            disableForm: false,
            searchIndicator: true,
          });
        });
    }

    const otherControls: Array<'clients' | 'zones' | 'products'> = [
      'clients',
      'zones',
      'products',
    ];

    otherControls.forEach((key) => {
      const ctrl = this.filtersForm.get(key);
      if (!ctrl) return;
      ctrl.valueChanges
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.persistFilters();
          this.pageIndex = 0;
          this.isLoading = true;
          this.filtersForm.disable({ emitEvent: false });
          this.loadPage(0);
        });
    });
  }

  async getProductsFilter(): Promise<void> {
    this.loadingProducts = true;
    const rawProducts = await firstValueFrom(
      this.historyService.getProductsForFilters()
    );

    this.products = (rawProducts ?? []).map((p: ProductFilterItem) => {
      const display: ProductFilterDisplay = {
        ...p,
        ProductLabel: this.translateProductKey(p.ProductKey),
      };
      return display;
    });

    this.loadingProducts = false;
  }

  private persistFilters(): void {
  const filters = this.filtersForm.getRawValue();

  const toSave = {
    search: typeof filters.search === 'string' ? filters.search : '',
    zones: Array.isArray(filters.zones) ? filters.zones : [],
    clients: Array.isArray(filters.clients) ? filters.clients : null,
    products: Array.isArray(filters.products) ? filters.products : [],
  };

  this.filtersPersistence.save(
    LocalStorageKeys.PENDINGSERVICESFILTERS,
    toSave
  );
}


  private retranslateProductLabels(lang: string): void {
    this.products = this.products.map((p) => ({
      ...p,
      ProductLabel: this.translateProductKey(p.ProductKey),
    }));
  }

  private getInitialDateRange(): [Date, Date] {
    const today = new Date();
    const raw = this.localStorageService.get(
      LocalStorageKeys.PENDINGSERVICESDATERANGE
    ) as string | null;

    if (raw) {
      try {
        const parsed = JSON.parse(raw) as [string, string];
        return [new Date(parsed[0]), new Date(parsed[1])];
      } catch {}
    }

    return [today, today];
  }

  private loadLookupsAndFirstPage([startDate, _endDate]: [Date, Date]): void {
    this.isLoading = true;
    this.isInitializing = true;
    this.filtersForm.disable({ emitEvent: false });

    forkJoin({
      zones: this.historyService.getZones().pipe(
        catchError((err) => {
          console.error('[PendingServices] load zones error:', err);
          return of(this.zones);
        })
      ),
      clients: this.historyService.getClients().pipe(
        catchError((err) => {
          console.error('[PendingServices] load clients error:', err);
          return of(this.allClients);
        })
      ),
    })
      .pipe(
        finalize(() => {
          this.isInitializing = false;
          this.filtersForm.enable({ emitEvent: false });
        })
      )
      .subscribe({
        next: ({ zones, clients }) => {
          this.zones = Array.isArray(zones) ? zones : zones?.zones ?? [];

          if (Array.isArray(clients)) {
            this.allClients = clients;
          } else if (clients && clients.businessUserData) {
            this.allClients = clients.businessUserData;
          } else {
            this.allClients = [];
          }

          this.loadPage(0);
        },
        error: (err: unknown) => {
          this.onLoadError(err);
        },
      });
  }

  private onLoadError(err: unknown): void {
    console.error('[PendingServices] load error:', err);
    const message = this.translate.instant(
      'service_manager.pending_service.load_error_message'
    );
    const label = this.translate.instant(
      'service_manager.pending_service.load_error_label'
    );
    this.alerts
      .open(message, { label, status: 'error', autoClose: true })
      .subscribe();
  }

  private getControlArrayValue(controlName: string): string[] {
    const rawValue = this.filtersForm.get(controlName)?.value as unknown;
    return Array.isArray(rawValue) ? (rawValue as string[]) : [];
  }

  private getSelectedClientGuid(): string | null {
    const raw = this.filtersForm.get('clients')?.value as unknown;
    if (!raw) return null;
    if (typeof raw === 'string') {
      const match = this.allClients.find((c) => c.businessName === raw);
      return match?.userId ?? null;
    }
    return null;
  }

  private loadPage(
    page: number,
    options?: {
      showLoader?: boolean;
      disableForm?: boolean;
      searchIndicator?: boolean;
    }
  ): void {
    const doShowLoader = options?.showLoader !== false;
    const doDisableForm = options?.disableForm !== false;
    const doSearchIndicator = options?.searchIndicator === true;

    const dateRange = this.dateRangeControl.value;
    if (!dateRange) {
      if (doShowLoader) this.isLoading = false;
      if (doDisableForm) this.filtersForm.enable({ emitEvent: false });
      if (doSearchIndicator) this.isSearching = false;
      return;
    }

    const [fromD, toD] = dateRange;

    const raw = this.filtersForm.getRawValue() as {
      search?: string | null;
    };
    const search = (raw.search ?? '').trim();
    const serviceFilter = search.length ? search : null;

    const request: PaginatedRequest = {
      clientGuid: this.getSelectedClientGuid(),
      fromDate: moment(fromD).format('YYYY-MM-DD'),
      toDate: moment(toD).format('YYYY-MM-DD'),
      pageNumber: page + 1,
      pageSize: this.pageSize,
      zonesGuid: this.getControlArrayValue('zones'),
      productsName: this.getControlArrayValue('products'),
      serviceFilter,
    };

    if (doShowLoader) this.isLoading = true;
    if (doDisableForm) this.filtersForm.disable({ emitEvent: false });
    if (doSearchIndicator) this.isSearching = true;

    this.pendingServicesService
      .getPaginatedPendingServices(request)
      .pipe(
        catchError((err) => {
          console.error('[PendingServices] getPaginated error', err);
          this.alerts
            .open(
              this.translate.instant(
                'service_manager.pending_service.load_error_message'
              ),
              { status: 'error', autoClose: true }
            )
            .subscribe();
          const empty: PaginatedResponse = {
            services: [],
            totalItems: 0,
            hasNextPage: false,
            hasPreviousPage: false,
          };
          return of(empty);
        }),
        finalize(() => {
          if (doShowLoader) this.isLoading = false;
          if (doDisableForm) this.filtersForm.enable({ emitEvent: false });
          if (doSearchIndicator) this.isSearching = false;
        })
      )
      .subscribe((resp: PaginatedResponse) => {
        this.pendingRows = (resp.services ?? []).map((s) => this.mapRow(s));
        const totalItemsValue = Number(resp.totalItems ?? 0);
        this.totalItems = totalItemsValue;
        this.totalPages = Math.max(
          1,
          Math.ceil(totalItemsValue / this.pageSize)
        );
        this.pageIndex = Math.min(page, Math.max(0, this.totalPages - 1));
        this.recountCommunication();
        this.applyLocalSearchAndSort();
      });
  }

  private applyLocalSearchAndSort(): void {
    const raw = this.filtersForm.getRawValue() as {
      search?: string | null;
      zones?: string[] | null;
      products?: string[] | null;
      clients?: string | null;
    };

    this.filteredRows = this.pendingRows;

    if (this.isSelectAllActive) {
      this.filteredRows
        .filter((r) => this.hasCommunication(r))
        .forEach((r) => {
          if (!this.manuallyDeselectedIds.has(r.serviceGuid)) {
            r.selected = true;
          } else {
            r.selected = false;
          }
        });
    }

    this.sortAndPaginate();
  }

  private watchDateRangeChanges(): void {
    this.dateRangeControl.valueChanges
      .pipe(
        debounceTime(250),
        tap(() => {
          this.isLoading = true;
          ['search', 'zones', 'clients', 'products'].forEach((ctrl) =>
            this.filtersForm.get(ctrl)!.disable({ emitEvent: false })
          );
        }),
        switchMap((range) => {
          // if cleared, stop and restore UI
          if (!range) {
            this.isLoading = false;
            this.filtersForm.enable({ emitEvent: false });
            return of(null);
          }

          const [fromD, toD] = range;
          const from = moment(fromD).startOf('day');
          const to = moment(toD).endOf('day');

          if (to.diff(from, 'months', true) > 1) {
            this.alerts
              .open(
                this.translate.instant(
                  'service_manager.pending_service.max_date_range_exceeded',
                  { months: 1 }
                ),
                { status: 'error', autoClose: true }
              )
              .subscribe();

            const prevRaw = this.localStorageService.get(
              LocalStorageKeys.PENDINGSERVICESDATERANGE
            ) as string | null;
            if (prevRaw) {
              try {
                const parsed = JSON.parse(prevRaw) as [string, string];
                this.dateRangeControl.setValue(
                  [new Date(parsed[0]), new Date(parsed[1])],
                  { emitEvent: false }
                );
              } catch {
                // ignore parse error
              }
            }

            this.isLoading = false;
            this.filtersForm.enable({ emitEvent: false });

            return of(null);
          }

          this.localStorageService.set(
            LocalStorageKeys.PENDINGSERVICESDATERANGE,
            JSON.stringify([fromD.toISOString(), toD.toISOString()])
          );

          this.pageIndex = 0;
          this.loadPage(0);
          return of(null);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {});
  }

  activeCommStates(
    c: Communication | undefined
  ): { icon: string; hintKey: string; color: string }[] {
    if (!c) return [];

    const states: CommunicationState[] = [
      'SMS_SENT',
      'SMS_ERROR',
      'WHATSAPP_WAITING',
      'WHATSAPP_ERROR',
      'WHATSAPP_NO_ANSWER',
      'WHATSAPP_ANSWERED',
      'PHONE_INCIDENT',
    ];

    return states
      .filter((s) => {
        switch (s) {
          case 'SMS_SENT':
            return c.smsSent;
          case 'SMS_ERROR':
            return c.smsSendingError;
          case 'WHATSAPP_WAITING':
            return c.whatsAppWaitingAnswer;
          case 'WHATSAPP_ERROR':
            return c.whatsAppErrorSending;
          case 'WHATSAPP_NO_ANSWER':
            return c.whatsAppNotAnsweredInTime;
          case 'WHATSAPP_ANSWERED':
            return c.whatsAppAnswered;
          case 'PHONE_INCIDENT':
            return (c as { phoneIncident?: boolean }).phoneIncident ?? false;
          default:
            return false;
        }
      })
      .map(
        (s) =>
          (
            COMM_ICONS as Record<
              CommunicationState,
              { icon: string; hintKey: string; color: string }
            >
          )[s]
      );
  }

  private recountCommunication(): void {
    const allStates: CommunicationState[] = [
      'NOT_CONTACTED',
      'PHONE_INCIDENT',
      'SMS',
      'SMS_SENT',
      'SMS_ERROR',
      'WHATSAPP',
      'WHATSAPP_WAITING',
      'WHATSAPP_ANSWERED',
      'WHATSAPP_NO_ANSWER',
      'WHATSAPP_ERROR',
    ];

    const counters: Record<CommunicationState, number> = {} as Record<
      CommunicationState,
      number
    >;
    allStates.forEach((s) => (counters[s] = 0));

    const checks: Array<[CommunicationState, (c?: Communication) => boolean]> =
      [
        ['SMS', (c) => !!c && (c.smsSent || c.smsSendingError)],
        [
          'WHATSAPP',
          (c) =>
            !!c &&
            (c.whatsAppAnswered ||
              c.whatsAppWaitingAnswer ||
              c.whatsAppErrorSending ||
              c.whatsAppNotAnsweredInTime),
        ],
        ['SMS_SENT', (c) => !!c?.smsSent],
        ['SMS_ERROR', (c) => !!c?.smsSendingError],
        ['WHATSAPP_WAITING', (c) => !!c?.whatsAppWaitingAnswer],
        ['WHATSAPP_ERROR', (c) => !!c?.whatsAppErrorSending],
        ['WHATSAPP_NO_ANSWER', (c) => !!c?.whatsAppNotAnsweredInTime],
        ['WHATSAPP_ANSWERED', (c) => !!c?.whatsAppAnswered],
      ];

    for (const row of this.pendingRows) {
      const c = row.communication;

      if (!c || checks.every(([_, cond]) => !cond(c))) {
        counters.NOT_CONTACTED++;
      }

      if ((row as { eventlog_incidence?: boolean }).eventlog_incidence) {
        counters.PHONE_INCIDENT++;
      }

      for (const [state, cond] of checks) {
        if (cond(c)) {
          counters[state]++;
        }
      }
    }

    this.commCounters = counters;
    this.commCountersSorted = Object.entries(counters)
      .map(([key, value]) => ({
        key: key as CommunicationState,
        value,
        color: (COMM_PALETTE as Record<CommunicationState, string>)[
          key as CommunicationState
        ],
        label: this.translate.instant('communication.' + key),
      }))
      .sort((a, b) => b.value - a.value);
  }

  private mapRow(service: PendingService): PendingServiceRow {
    let rawType = service.serviceType;
    let category = '';
    if (rawType && SERVICE_TYPE_TRANSLATION_KEY[rawType]) {
      category = this.translate.instant(SERVICE_TYPE_TRANSLATION_KEY[rawType]);
    }
    const startHour = service.serviceDate?.time
      ? moment(service.serviceDate.time, 'HH:mm:ss').format('HH:mm')
      : '--';

    const endHour = service.endDate?.time
      ? moment(service.endDate.time, 'HH:mm:ss').format('HH:mm')
      : '--';

    let momentDate: moment.Moment | null = null;
    let formattedDate = '--';

    if (service.serviceDate?.completeDate) {
      const [isoPart] = service.serviceDate.completeDate.split(' ');
      momentDate = moment.parseZone(isoPart);
    } else if (service.serviceDate?.date) {
      momentDate = moment(service.serviceDate.date, 'dddd, DD MMMM YYYY', true);
    }

    if (momentDate?.isValid()) {
      formattedDate = momentDate.format('DD/MM/YYYY');
    } else {
      momentDate = null;
    }

    const zone = this.zones.find((z) => z.zoneId === service.zoneGuid);
    const driversName = Array.isArray(service.driversName)
      ? service.driversName.filter((name) => !!name?.trim())
      : [];
    const driversNameDisplay = driversName.length
      ? driversName.join(', ')
      : '-';

    return {
      ...service,
      zoneName: zone?.zoneName ?? '',
      formattedDate,
      startHour,
      endHour,
      selected: false,
      contactPhoneNumberHint: service.isExternalContact
        ? this.translate.instant(
            'service_manager.pending_service.external_contact_input_table'
          )
        : this.translate.instant(
            'service_manager.pending_service.internal_contact_input_table'
          ),
      driversName,
      driversNameDisplay,
      _moment: momentDate,
      category,
    } as PendingServiceRow;
  }

  onChangePage(index: number): void {
    this.pageIndex = index;
    this.loadPage(index);
  }

  onSort(column: keyof PendingServiceRow): void {
    this.pageIndex = 0;

    if (this.sortColumn !== column) {
      this.sortColumn = column;
      this.sortDirection = 1;
    } else if (this.sortDirection === 1) {
      this.sortDirection = -1;
    } else {
      this.sortColumn = '';
      this.sortDirection = 1;
    }

    this.applyLocalSearchAndSort();
  }

  toggleSelectAll(checked: boolean): void {
    this.isSelectAllActive = checked;
    
    if (checked) {
      this.manuallyDeselectedIds.clear();
    }
    
    this.filteredRows
      .filter((r) => this.hasCommunication(r))
      .forEach((r) => (r.selected = checked));
  }

  openDuplicateModal(): void {
    const offcanvasRef = this.offcanvas.open(ModalDuplicateServiceComponent, {
      backdrop: 'static',
      position: 'end',
    });

    offcanvasRef.componentInstance.type_info = 'ad';

    offcanvasRef.componentInstance.finished
      .pipe(takeUntil(this.destroy$))
      .subscribe((wasSuccessful: boolean) => {
        if (wasSuccessful) {
          this.loadPage(0);
        }
      });
  }

  openSendMessage(): void {
    if (!this.sendingMethod) {
      this.alerts
        .open(
          this.translate.instant(
            'service_manager.pending_service.select_send_method'
          ),
          {
            label: this.translate.instant(
              'service_manager.pending_service.warning_label'
            ),
            status: 'warning',
            autoClose: true,
          }
        )
        .subscribe();
      return;
    }

    this.sending = true;
    const selected = this.selectedRows();
    const ref = this.offcanvas.open(ModalSendMessageComponent, {
      backdrop: 'static',
      position: 'end',
      panelClass: 'offcanvas-width-450',
    });

    const modal = ref.componentInstance;
    modal.sendType = this.sendingMethod;
    modal.serviciosSeleccionados = selected;
    modal.zones = this.zones;
    modal.baseInfo = { count: selected.length };

    modal.finished.subscribe((wasSuccessful: boolean) => {
      this.sending = false;
      if (wasSuccessful) {
        this.displayedRows.forEach((row) => (row.selected = false));
        this.loadPage(0);
      }
    });
  }

  get canSelectAny(): boolean {
    return this.displayedRows.some((row) => this.hasCommunication(row));
  }

  get exportFormatCount(): number {
    return (this.exportXlsx ? 1 : 0) + (this.exportCsv ? 1 : 0);
  }

  shouldShowCategoryColumn(): boolean {
    return this.displayedRows.some(row => row.serviceType && row.serviceType !== 0);
  }
 
  async exportSelected(): Promise<void> {
    if (!this.exportCsv && !this.exportXlsx) {
      return;
    }

    const dateRange = this.dateRangeControl.value;
    if (!dateRange) {
      this.alerts
        .open(
          this.translate.instant(
            'service_manager.pending_service.no_date_range'
          ),
          { status: 'error', autoClose: true }
        )
        .subscribe();
      return;
    }

    if (this.exportInProgress) {
      return;
    }

    this.exportInProgress = true;

    try {
      const services = await this.fetchAllServicesForCurrentFilters();

      const rows = (services ?? []).map((s) => this.mapRow(s));

      if (!rows.length) {
        this.alerts
          .open(
            this.translate.instant(
              'service_manager.pending_service.no_data_to_export'
            ),
            {
              status: 'warning',
              autoClose: true,
            }
          )
          .subscribe();
        return;
      }

      if (this.exportCsv) {
        this.generateCsvFromRows(rows);
      }
      if (this.exportXlsx) {
        this.generateXlsxFromRows(rows);
      }

      this.alerts
        .open(
          this.translate.instant(
            'service_manager.pending_service.export_completed'
          ),
          {
            status: 'success',
            autoClose: true,
          }
        )
        .subscribe();
    } catch (err) {
      console.error('[PendingServices] export error', err);
      this.alerts
        .open(
          this.translate.instant(
            'service_manager.pending_service.error_fetching_export'
          ),
          {
            status: 'error',
            autoClose: true,
          }
        )
        .subscribe();
    } finally {
      this.exportInProgress = false;
    }
  }

  private async fetchAllServicesForCurrentFilters(): Promise<PendingService[]> {
    const dateRange = this.dateRangeControl.value!;
    const [fromD, toD] = dateRange;
    const raw = this.filtersForm.getRawValue() as { search?: string | null };
    const search = (raw.search ?? '').trim();
    const serviceFilter = search.length ? search : null;

    const params: {
      clientGuid?: string | null;
      fromDate: string;
      toDate: string;
      zonesGuid?: string[];
      productsName?: string[];
      serviceFilter?: string | null;
    } = {
      clientGuid: this.getSelectedClientGuid(),
      fromDate: moment(fromD).format('YYYY-MM-DD'),
      toDate: moment(toD).format('YYYY-MM-DD'),
      zonesGuid: this.getControlArrayValue('zones'),
      productsName: this.getControlArrayValue('products'),
    };
    if (serviceFilter) params.serviceFilter = serviceFilter;

    try {
      const svcAny = this.pendingServicesService as any;

      if (typeof svcAny.getAllPendingServices === 'function') {
        const obs = svcAny.getAllPendingServices(params);
        const services = await firstValueFrom(obs);
        return Array.isArray(services) ? services : [];
      }

      if (typeof svcAny.getPendingServices === 'function') {
        const obs = svcAny.getPendingServices(params);
        const resp = await firstValueFrom(obs);
        if (resp && Array.isArray(resp)) {
          return resp as PendingService[];
        }
        if (resp && Array.isArray((resp as any).services)) {
          return (resp as any).services as PendingService[];
        }
        return Array.isArray(resp) ? resp : [];
      }

      throw new Error(
        'PendingServicesService does not expose getAllPendingServices or getPendingServices. Adapt fetchAllServicesForCurrentFilters to your service.'
      );
    } catch (err) {
      console.error(
        '[PendingServices] fetchAllServicesForCurrentFilters error',
        err
      );
      throw err;
    }
  }

  private generateXlsxFromRows(rows: PendingServiceRow[]): void {
    const t = this.translate;
    const today = moment().tz('Europe/Madrid').format('DD/MM/YYYY');
    const csvTpl = t.instant(
      'service_manager.pending_service.export_filename',
      {
        date: today,
      }
    );
    const filename = csvTpl.replace(/\.csv$/, '.xlsx');

    const headers = [
      t.instant('service_manager.pending_service.plate_input_table'),
      t.instant('service_manager.pending_service.hour_input_table'),
      t.instant('service_manager.pending_service.hash_input_table'),
      t.instant('service_manager.pending_service.zone_input_table'),
      t.instant('service_manager.pending_service.date_input_table'),
      t.instant('service_manager.pending_service.product_input_table'),
      t.instant('service_manager.pending_service.isAPoolService_input_table'),
      t.instant('service_manager.pending_service.toppings_input_table'),
      t.instant('service_manager.pending_service.client_input_table'),
      t.instant('service_manager.pending_service.phoneNumber_input_table'),
      t.instant('service_manager.pending_service.communication_input_table'),
      t.instant('service_manager.pending_service.pickup_address_input_table'),
      t.instant('service_manager.pending_service.provider_address_input_table'),
      t.instant('service_manager.pending_service.delivery_input_table'),
      t.instant('service_manager.pending_service.comments_input_table'),
      t.instant('service_manager.pending_service.custom_fields_input_table'),
      t.instant('service_manager.pending_service.tags_input_table'),
      t.instant('service_manager.pending_service.km_input_table'),
      t.instant(
        'service_manager.pending_service.favorite_pickup_address_input_table'
      ),
      t.instant(
        'service_manager.pending_service.favorite_delivery_address_input_table'
      ),
      t.instant('service_manager.pending_service.node_hour_input_table'),
      t.instant('service_manager.pending_service.additional_cost_input_table'),
      t.instant('service_manager.pending_service.vehicle_type_input_table'),
      t.instant('service_manager.pending_service.vehicle_brand_input_table'),
      t.instant('service_manager.pending_service.vehicle_model_input_table'),
      t.instant(
        'service_manager.pending_service.vehicle_license_plate_input_table'
      ),
      t.instant('service_manager.pending_service.vehicle_fuel_type_input_table'),
      t.instant('service_manager.pending_service.approved_expenses_input_table'),
      t.instant('service_manager.pending_service.totalPrice_input_table'),
      t.instant('service_manager.pending_service.price_without_vat'),
    ];

    const aoa = [
      headers,
      ...rows.map((r) => {
        const commLabels =
          this.activeCommStates(r.communication)
            .map((v) => t.instant(v.hintKey))
            .join(', ') ||
          t.instant('service_manager.pending_service.unassigned');

        return [
          r.licensePlate,
          `${r.startHour} - ${r.endHour}`,
          r.orderHash,
          r.zoneName,
          r.formattedDate,
          this.translateProduct(r.productName),
          r.isAPoolService
            ? t.instant('service_manager.pending_service.in_pool')
            : t.instant('service_manager.pending_service.not_in_pool'),
          r.toppingNames.length
            ? r.toppingNames.join(', ')
            : t.instant('service_manager.pending_service.no_toppings'),
          r.clientName,
          r.phoneNumber ?? '',
          commLabels,
          r.originAddress ||
            t.instant('service_manager.pending_service.no_address'),
          r.providerAddress ?? '',
          r.destinationAddress ||
            t.instant('service_manager.pending_service.no_address'),
          r.additionalInformation
            ? r.additionalInformation
            : t.instant('service_manager.pending_service.no_comments'),
          this.getCustomFieldsValue(r) ||
            t.instant('service_manager.pending_service.no_data'),
          this.getTagsValue(r) ||
            t.instant('service_manager.pending_service.no_data'),
          this.getKmValue(r) || t.instant('service_manager.pending_service.no_data'),
          this.getFavoritePickupAddressValue(r) ||
            t.instant('service_manager.pending_service.no_data'),
          this.getFavoriteDeliveryAddressValue(r) ||
            t.instant('service_manager.pending_service.no_data'),
          this.getNodeHourValue(r) ||
            t.instant('service_manager.pending_service.no_data'),
          this.getAdditionalCostValue(r) ||
            t.instant('service_manager.pending_service.no_data'),
          this.getVehicleTypeValue(r) ||
            t.instant('service_manager.pending_service.no_data'),
          this.getVehicleBrandValue(r) ||
            t.instant('service_manager.pending_service.no_data'),
          this.getVehicleModelValue(r) ||
            t.instant('service_manager.pending_service.no_data'),
          this.getVehicleLicensePlateValue(r) ||
            t.instant('service_manager.pending_service.no_data'),
          this.getVehicleFuelTypeValue(r) ||
            t.instant('service_manager.pending_service.no_data'),
          r.approvedExpensesAmount != null
            ? `${r.approvedExpensesAmount.toFixed(2)} ${r.currencySymbol}`
            : '',
          r.totalPrice != null
            ? `${r.totalPrice.toFixed(2)} ${r.currencySymbol}`
            : '',
          r.totalPriceWithoutTax != null
            ? `${r.totalPriceWithoutTax.toFixed(2)} ${r.currencySymbol}`
            : '',
        ].map((cell) => this.dashIfEmpty(cell));
      }),
    ];

    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [
      { wch: 12 }, // Matricula
      { wch: 14 }, // Hora
      { wch: 14 }, // Hash
      { wch: 14 }, // Zona
      { wch: 12 }, // Fecha
      { wch: 18 }, // Producto
      { wch: 14 }, // Servicio en pool
      { wch: 22 }, // Toppings
      { wch: 18 }, // Cliente
      { wch: 20 }, // Telefono
      { wch: 18 }, // Comunicacion
      { wch: 36 }, // Direccion recogida
      { wch: 36 }, // Direccion proveedor
      { wch: 36 }, // Direccion entrega
      { wch: 28 }, // Comentarios
      { wch: 30 }, // Campos personalizados
      { wch: 30 }, // Tags
      { wch: 10 }, // KM
      { wch: 28 }, // Direccion recogida favorita
      { wch: 28 }, // Direccion entrega favorita
      { wch: 12 }, // Hora nodo
      { wch: 16 }, // Coste adicional
      { wch: 16 }, // Tipo vehiculo
      { wch: 16 }, // Marca vehiculo
      { wch: 16 }, // Modelo vehiculo
      { wch: 18 }, // Matricula vehiculo
      { wch: 16 }, // Tipo combustible
      { wch: 16 }, // Gastos aprobados
      { wch: 12 }, // Precio total
      { wch: 14 }, // Precio sin IVA
    ];
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Pendientes');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

    saveAs(new Blob([wbout]), filename);
  }

  private generateCsvFromRows(rows: PendingServiceRow[]): void {
    const t = this.translate;
    const today = moment().tz('Europe/Madrid').format('DD/MM/YYYY');

    const filename = t.instant(
      'service_manager.pending_service.export_filename',
      { date: today }
    );

    const headers = [
      t.instant('service_manager.pending_service.plate_input_table'),
      t.instant('service_manager.pending_service.hour_input_table'),
      t.instant('service_manager.pending_service.hash_input_table'),
      t.instant('service_manager.pending_service.zone_input_table'),
      t.instant('service_manager.pending_service.date_input_table'),
      t.instant('service_manager.pending_service.product_input_table'),
      t.instant('service_manager.pending_service.isAPoolService_input_table'),
      t.instant('service_manager.pending_service.toppings_input_table'),
      t.instant('service_manager.pending_service.client_input_table'),
      t.instant('service_manager.pending_service.phoneNumber_input_table'),
      t.instant('service_manager.pending_service.communication_input_table'),
      t.instant('service_manager.pending_service.pickup_address_input_table'),
      t.instant('service_manager.pending_service.provider_address_input_table'),
      t.instant('service_manager.pending_service.delivery_input_table'),
      t.instant('service_manager.pending_service.comments_input_table'),
      t.instant('service_manager.pending_service.custom_fields_input_table'),
      t.instant('service_manager.pending_service.tags_input_table'),
      t.instant('service_manager.pending_service.km_input_table'),
      t.instant(
        'service_manager.pending_service.favorite_pickup_address_input_table'
      ),
      t.instant(
        'service_manager.pending_service.favorite_delivery_address_input_table'
      ),
      t.instant('service_manager.pending_service.node_hour_input_table'),
      t.instant('service_manager.pending_service.additional_cost_input_table'),
      t.instant('service_manager.pending_service.vehicle_type_input_table'),
      t.instant('service_manager.pending_service.vehicle_brand_input_table'),
      t.instant('service_manager.pending_service.vehicle_model_input_table'),
      t.instant(
        'service_manager.pending_service.vehicle_license_plate_input_table'
      ),
      t.instant('service_manager.pending_service.vehicle_fuel_type_input_table'),
      t.instant('service_manager.pending_service.approved_expenses_input_table'),
      t.instant('service_manager.pending_service.totalPrice_input_table'),
      t.instant('service_manager.pending_service.price_without_vat'),
    ].map(this.escapeCsv);

    const rowsContent = rows.map((r) => {
      const commLabels =
        this.activeCommStates(r.communication)
          .map((v) => t.instant(v.hintKey))
          .join(', ') ||
        t.instant('service_manager.pending_service.unassigned');

      const approvedExpensesCell =
        r.approvedExpensesAmount != null
          ? this.escapeCsv(
              `${r.approvedExpensesAmount.toFixed(2)} ${r.currencySymbol}`
            )
          : '';
      const totalPriceCell =
        r.totalPrice != null
          ? this.escapeCsv(`${r.totalPrice.toFixed(2)} ${r.currencySymbol}`)
          : '';
      const priceWithoutVatCell =
        r.totalPriceWithoutTax != null
          ? this.escapeCsv(
              `${r.totalPriceWithoutTax.toFixed(2)} ${r.currencySymbol}`
            )
          : '';

      return [
        r.licensePlate,
        `${r.startHour} - ${r.endHour}`,
        r.orderHash,
        r.zoneName,
        r.formattedDate,
        this.translateProduct(r.productName),
        r.isAPoolService
          ? t.instant('service_manager.pending_service.in_pool')
          : t.instant('service_manager.pending_service.not_in_pool'),
        r.toppingNames.length
          ? r.toppingNames.join(', ')
          : t.instant('service_manager.pending_service.no_toppings'),
        r.clientName,
        r.phoneNumber ?? '',
        commLabels,
        r.originAddress ||
          t.instant('service_manager.pending_service.no_address'),
        r.providerAddress ?? '',
        r.destinationAddress ||
          t.instant('service_manager.pending_service.no_address'),
        r.additionalInformation
          ? r.additionalInformation
          : t.instant('service_manager.pending_service.no_comments'),
        this.getCustomFieldsValue(r) ||
          t.instant('service_manager.pending_service.no_data'),
        this.getTagsValue(r) ||
          t.instant('service_manager.pending_service.no_data'),
        this.getKmValue(r) || t.instant('service_manager.pending_service.no_data'),
        this.getFavoritePickupAddressValue(r) ||
          t.instant('service_manager.pending_service.no_data'),
        this.getFavoriteDeliveryAddressValue(r) ||
          t.instant('service_manager.pending_service.no_data'),
        this.getNodeHourValue(r) ||
          t.instant('service_manager.pending_service.no_data'),
        this.getAdditionalCostValue(r) ||
          t.instant('service_manager.pending_service.no_data'),
        this.getVehicleTypeValue(r) ||
          t.instant('service_manager.pending_service.no_data'),
        this.getVehicleBrandValue(r) ||
          t.instant('service_manager.pending_service.no_data'),
        this.getVehicleModelValue(r) ||
          t.instant('service_manager.pending_service.no_data'),
        this.getVehicleLicensePlateValue(r) ||
          t.instant('service_manager.pending_service.no_data'),
        this.getVehicleFuelTypeValue(r) ||
          t.instant('service_manager.pending_service.no_data'),
        approvedExpensesCell,
        totalPriceCell,
        priceWithoutVatCell,
      ].map((cell) => this.escapeCsv(this.dashIfEmpty(cell)));
    });

    const csvContent = [headers, ...rowsContent]
      .map((line) => line.join(','))
      .join('\n');

    saveAs(
      new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }),
      filename
    );
  }

  selectedRows(): PendingServiceRow[] {
    return this.filteredRows.filter((r) => r.selected);
  }

  private escapeCsv(value: string): string {
    return value.includes(',') || value.includes('"') || value.includes('\n')
      ? `"${value.replace(/"/g, '""')}"`
      : value;
  }

  private dashIfEmpty(value: unknown): string {
    if (value === null || value === undefined) return '-';
    const normalized = String(value).trim();
    return normalized.length ? normalized : '-';
  }

  private firstNonEmptyValue(values: unknown[]): string {
    for (const value of values) {
      if (value === null || value === undefined) continue;
      if (typeof value === 'number') return String(value);
      if (typeof value === 'string' && value.trim().length > 0)
        return value.trim();
    }
    return '';
  }

  private getMetadataItems(
    row: PendingServiceRow
  ): Array<{ id: string; tag: string }> {
    const raw = row as any;
    const list = Array.isArray(raw.metadataTags)
      ? raw.metadataTags
      : Array.isArray(raw.MetadataTags)
      ? raw.MetadataTags
      : Array.isArray(raw.Tags)
      ? raw.Tags
      : Array.isArray(raw.tags)
      ? raw.tags
      : [];

    return list.map((item: any) => ({
      id:
        typeof item?.id === 'string'
          ? item.id.trim()
          : typeof item?.Id === 'string'
          ? item.Id.trim()
          : '',
      tag:
        typeof item?.tag === 'string'
          ? item.tag.trim()
          : typeof item?.Tag === 'string'
          ? item.Tag.trim()
          : '',
    }));
  }

  // Campos personalizados: elementos cuyo "tag" tiene contenido. Formato "{id}: {tag}", uno por línea.
  private getCustomFieldsValue(row: PendingServiceRow): string {
    return this.getMetadataItems(row)
      .filter((item) => item.tag.length > 0)
      .map((item) => `${item.id}: ${item.tag}`)
      .join('\n');
  }

  // Tags: elementos cuyo "tag" es cadena vacía. Se concatena el "id" separado por comas.
  private getTagsValue(row: PendingServiceRow): string {
    return this.getMetadataItems(row)
      .filter((item) => item.tag.length === 0)
      .map((item) => item.id)
      .filter((id) => id.length > 0)
      .join(', ');
  }

  private getKmValue(row: PendingServiceRow): string {
    const raw = row as any;
    const meters = Number(raw.distanceInMeters);
    if (!Number.isFinite(meters)) return '';

    const km = meters / 1000;
    return Number.isInteger(km) ? String(km) : km.toFixed(2);
  }

  private getFavoritePickupAddressValue(row: PendingServiceRow): string {
    const raw = row as any;
    return this.firstNonEmptyValue([raw.clientStartAddressKey]);
  }

  private getFavoriteDeliveryAddressValue(row: PendingServiceRow): string {
    const raw = row as any;
    return this.firstNonEmptyValue([raw.clientFinishAddressKey]);
  }

  private getNodeHourValue(row: PendingServiceRow): string {
    const raw = row as any;
    const nodeDates = Array.isArray(raw.NodeDates)
      ? raw.NodeDates
      : Array.isArray(raw.nodeDates)
      ? raw.nodeDates
      : [];

    const candidate = this.firstNonEmptyValue([
      ...nodeDates.map(
        (node: any) =>
          node?.AssignedStartDate?.time ?? node?.assignedStartDate?.time
      ),
      ...nodeDates.map(
        (node: any) =>
          node?.AssignedStartDate?.completeDate ??
          node?.assignedStartDate?.completeDate
      ),
      raw.NodeDates?.AssignedStartDate?.time,
      raw.NodeDates?.assignedStartDate?.time,
      raw.nodeDates?.AssignedStartDate?.time,
      raw.nodeDates?.assignedStartDate?.time,
    ]);

    if (!candidate) return '';

    if (typeof candidate === 'string') {
      const asHour = moment(candidate, ['HH:mm:ss', 'HH:mm'], true);
      if (asHour.isValid()) return asHour.format('HH:mm');

      const asDate = moment(candidate);
      if (asDate.isValid()) return asDate.format('HH:mm');

      return candidate;
    }

    return '';
  }

  private getAdditionalCostValue(row: PendingServiceRow): string {
    const raw = row as any;
    const purchasedServicesInfo = Array.isArray(raw.purchasedServicesInfo)
      ? raw.purchasedServicesInfo
      : [];

    const overdraftItems = purchasedServicesInfo.filter(
      (item: any) => item?.localizationKey === 'overdraft'
    );
    if (!overdraftItems.length) return '';

    const total = overdraftItems.reduce((sum: number, item: any) => {
      const price = Number(item?.price ?? 0);
      return Number.isFinite(price) ? sum + price : sum;
    }, 0);

    return `${total.toFixed(2)} ${row.currencySymbol ?? ''}`.trim();
  }

  private getVehicleTypeValue(row: PendingServiceRow): string {
    const raw = row as any;
    const rawType = raw.vehicleChassisType ?? raw.vehicleChasisType;
    const typeId = Number(rawType);

    if (Number.isFinite(typeId)) {
      const match = vehicleChasisTypeWithTranslations.find(
        (item) => item.value === typeId
      );
      if (match?.real_name) {
        return this.translate.instant(match.real_name);
      }
    }

    return this.firstNonEmptyValue([rawType, raw.vehicleCategory]);
  }

  private getVehicleBrandValue(row: PendingServiceRow): string {
    const raw = row as any;
    return this.firstNonEmptyValue([raw.vehicleBrand]);
  }

  private getVehicleModelValue(row: PendingServiceRow): string {
    const raw = row as any;
    return this.firstNonEmptyValue([raw.vehicleModel]);
  }

  private getVehicleLicensePlateValue(row: PendingServiceRow): string {
    const raw = row as any;
    return this.firstNonEmptyValue([raw.vehicleLicensePlate]);
  }

  private getVehicleFuelTypeValue(row: PendingServiceRow): string {
    const raw = row as any;
    const rawFuel = raw.vehicleFuelType;
    const fuelId = Number(rawFuel);

    if (Number.isFinite(fuelId)) {
      const labelKey = VehicleFuelLabelKeys[fuelId as VehicleFuelTypeEnum];
      if (labelKey) {
        return this.translate.instant(labelKey);
      }
    }

    return this.firstNonEmptyValue([rawFuel]);
  }

  get selectAll(): boolean {
    const commRows = this.filteredRows.filter((r) => this.hasCommunication(r));
    const allSelected = commRows.length > 0 && commRows.every((r) => r.selected);
    
    if (!allSelected && this.isSelectAllActive) {
      commRows.forEach((r) => {
        if (!r.selected) {
          this.manuallyDeselectedIds.add(r.serviceGuid);
        }
      });
    }
    
    return allSelected;
  }

  set selectAll(value: boolean) {
    this.toggleSelectAll(value);
  }

  toggleDropdown(): void {
    this.dropdownOpen = !this.dropdownOpen;
  }

  focusDropdownHost(): void {
    this.dropdown?.nativeFocusableElement?.focus();
  }

  translateProduct(name: string): string {
    const key = 'common.services.' + name;
    const translated = this.translate.instant(key);
    return translated === key ? name : translated;
  }

  translateProductKey(productKey: string): string {
    try {
      return (
        (this.historyService.productsCodes?.[this.translate.currentLang] ?? {})[
          productKey
        ] ?? productKey
      );
    } catch {
      return productKey;
    }
  }

  hasCommunication(row: PendingServiceRow): boolean {
    return Boolean(row.zoneGuid && row.productGuid);
  }

  private sortAndPaginate(): void {
    if (this.sortColumn) {
      const dir = this.sortDirection;
      const col = this.sortColumn as keyof PendingServiceRow;
      this.filteredRows.sort((a, b) => {
        const va = (a[col] ?? '') as string | number;
        const vb = (b[col] ?? '') as string | number;
        if (va < vb) return -dir;
        if (va > vb) return dir;
        return 0;
      });
    }

    this.displayedRows = [...this.filteredRows];
  }

  private matchCommunication(
    _row: PendingServiceRow,
    _filters: CommunicationState[]
  ): boolean {
    return true;
  }

  isRowLoading(hash: string): boolean {
    return this.loadingRows.has(hash);
  }

  viewDetail(row: PendingServiceRow): void {
    this.historyService.setData({ orderHash: row.orderHash });
    const tree: UrlTree = this.router.createUrlTree(
      ['/service-manager/detail-service'],
      {
        queryParams: { orderHash: row.orderHash },
      }
    );
    window.open(`${window.location.origin}/#/${tree}`, '_blank');
  }

  openModalEditService(row: PendingServiceRow): void {
    const hash = row.orderHash;
    this.loadingRows.add(hash);

    this.historyService
      .getServiceDetail(hash)
      .pipe(
        catchError((err) => {
          this.alerts
            .open(
              this.translate.instant(
                'service_manager.pending_service.error_loading_detail'
              ),
              { status: 'error' }
            )
            .subscribe();
          return of(null);
        }),
        finalize(() => {
          this.loadingRows.delete(hash);
        })
      )
      .subscribe((serviceInfo) => {
        if (!serviceInfo) return;

        const ref = this.offcanvas.open(ModalDuplicateServiceComponent, {
          backdrop: 'static',
          position: 'end',
        });
        ref.componentInstance.type_info = 'ed';
        ref.componentInstance.service_info = serviceInfo;
        ref.componentInstance.currency = row.currencySymbol;

        ref.componentInstance.finished.subscribe((wasSuccessful: boolean) => {
          if (!wasSuccessful) return;
          this.loadPage(0);
        });
      });
  }
}
